import { useMemo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/Drawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import { useForm } from 'react-hook-form'
import doCancelOrder from 'ducks/integrations/shopify/operations/doCancelOrder'
import { moneyString } from 'util/currency'
import { selectCurrentTicketGqlId } from 'selectors/tickets'

import { selectIntegrationCredentialsForId } from 'ducks/integrations'

import RefundTypeSelection from './RefundTypeSelection'
import CancelReasonDropdown from './CancelReasonDropdown'

import { styles } from './styles'

const ShopifyCancelOrderModal = ({
  onClose,
  onExit,
  open = false,
  drawerIntegrationId,
  drawerResourceId,
  unpaid,
  refundAmount,
  currencyCode,
}) => {
  const [isSending, setSending] = useState(false)
  const dispatch = useDispatch()

  const integration = useSelector(state => {
    return selectIntegrationCredentialsForId(state, {
      id: drawerIntegrationId,
    })
  })

  const conversationId = useSelector(selectCurrentTicketGqlId)

  const { handleSubmit, control, register } = useForm({
    mode: 'all',
    defaultValues: {
      restock: true,
      sendNotification: true,
      selectedReason: 'customer',
      type: 'refund-now',
    },
  })

  const onSubmit = useCallback(
    async data => {
      const payload = {
        reason: data.selectedReason,
        email: data.sendNotification,
        restock: data.restock,
        amount: data.type === 'refund-now' ? refundAmount : 0,
      }
      setSending(true)
      await dispatch(
        doCancelOrder(integration.id, drawerResourceId, conversationId, payload)
      )
      setSending(false)
      if (onClose) onClose()
    },
    [
      integration,
      drawerResourceId,
      dispatch,
      onClose,
      refundAmount,
      setSending,
      conversationId,
    ]
  )

  const refundAmountFormatted = moneyString({
    amount: refundAmount,
    currencyCode,
  })

  const DrawerForm = useCallback(
    props => <form onSubmit={handleSubmit(onSubmit)} {...props} />,
    [handleSubmit, onSubmit]
  )

  const footer = useMemo(
    () => {
      return (
        <ModalBtns
          warningBtnText={isSending ? 'Cancelling...' : 'Cancel order'}
          warningBtnHtmlType="submit"
          warningBtnDisabled={isSending}
          tertiaryBtnText="Keep order"
          onClickTertiaryBtn={onClose}
        />
      )
    },
    [onClose, isSending]
  )

  return (
    <Drawer
      title="Cancel order"
      footer={footer}
      onClose={onExit}
      open={open}
      css={text.styles.textDark}
      container={DrawerForm}
    >
      <div className="grui pt-10" css={styles.content}>
        {refundAmount > 0 && (
          <div>
            <div css={fieldStyles.labelBox}>Refund payment</div>
            <RefundTypeSelection
              control={control}
              name="type"
              unpaid={unpaid}
              refundAmountFormatted={refundAmountFormatted}
            />
          </div>
        )}

        <div>
          <div css={fieldStyles.labelBox}>Inventory</div>
          <Checkbox id="restockItems" {...register('restock')}>
            Restock items
          </Checkbox>
        </div>
        <div>
          <div css={fieldStyles.labelBox} className="grui mt-12">
            Reason for cancelation
          </div>
          <div css={styles.dropdownContainer}>
            <CancelReasonDropdown control={control} name="selectedReason" />
          </div>
        </div>
        <div>
          <div css={fieldStyles.labelBox}>Notification</div>
          <Checkbox id="sendNotification" {...register('sendNotification')}>
            Send a notification to the customer
          </Checkbox>
        </div>
      </div>
    </Drawer>
  )
}

export default ShopifyCancelOrderModal
